import AppSignal from '@appsignal/javascript';
import { plugin as breadcrumbsNetworkPlugin } from '@appsignal/plugin-breadcrumbs-network';
import { plugin as breadcrumbsConsolePlugin } from '@appsignal/plugin-breadcrumbs-console';
import { plugin as windowEventsPlugin } from '@appsignal/plugin-window-events';
import { plugin as pathDecoratorPlugin } from '@appsignal/plugin-path-decorator';

import { shouldRenameError } from 'server/utils/errors';
import { isServer } from 'utils/helpers/browser';
import { release } from '../ZmyleReleaseInfo.js';
import { ClientConfig } from './clientConfig';

interface SendErrorParams {
  error: Error;
  payload: Record<string, any>;
  tags?: Record<string, any>;
}

export interface IAppSignalClient {
  getClient(): AppSignal;
  sendError(params: SendErrorParams): void;
}

export class AppSignalClient implements IAppSignalClient {
  private appSignal: AppSignal;

  private static instance: IAppSignalClient;

  private constructor() {
    const clientConfig = ClientConfig.getInstance().getConfig();

    this.appSignal = new AppSignal({
      key: clientConfig.appSignalKey,
      revision: release ?? undefined,
    });

    this.appSignal.addDecorator(span => {
      if (isServer()) {
        span.setTags({ source: 'server' });
      } else {
        span.setTags({ source: 'browser', full_url: window.location.href });
      }

      return span;
    });

    if (typeof window === 'undefined') return;

    this.appSignal.use(breadcrumbsNetworkPlugin());
    this.appSignal.use(breadcrumbsConsolePlugin());
    this.appSignal.use(windowEventsPlugin());
    this.appSignal.use(pathDecoratorPlugin());

    const bindOnError = window.onerror;

    window.onerror = (message, source, lineno, colno, error) => {
      if (error && shouldRenameError(error)) {
        // eslint-disable-next-line no-param-reassign
        error.name = error.message;
      }

      if (bindOnError) bindOnError(message, source, lineno, colno, error);
    };
  }

  public static init() {
    AppSignalClient.instance = new AppSignalClient();
  }

  public static getInstance() {
    if (!AppSignalClient.instance) {
      AppSignalClient.instance = new AppSignalClient();
    }

    return AppSignalClient.instance;
  }

  public getClient() {
    return this.appSignal;
  }

  public sendError(params: SendErrorParams) {
    const { error, payload, tags = {} } = params;

    const span = this.appSignal.createSpan();

    if (shouldRenameError(error)) error.name = error.message;

    span.setError(error);
    span.setParams(payload);

    if (tags) span.setTags(tags);

    this.appSignal.send(span);
  }
}
